"use client";

// EditableText — the in-place rewrite primitive (the inline-edit LAW). At rest
// it's PLAIN text (no underline, no hover line). Double-click (or double-tap on
// touch) enters edit: the editable text becomes underlined to show it's live.
//
// Two modes:
//   • singleLine (names, titles): an auto-WIDTH field that grows with the text as
//     you type — so the navigator slider extends to the right and it always feels
//     live. Enter commits. The ✓ / ✕ sit inline at the right.
//   • multiline (summaries): an auto-HEIGHT textarea that shows the exact number
//     of lines and grows/shrinks as you type, with the ✓ / ✕ on the BOTTOM.
// Enter confirms (Shift+Enter = newline in multiline), Escape cancels. Leaves no
// chrome behind at rest.

import { useEffect, useRef, useState } from "react";

export default function EditableText({
  value,
  manual = false,
  singleLine = false,
  onSave,
  placeholder = "write your own…",
  style,
  className,
  title,
}: {
  value: string;
  manual?: boolean;
  singleLine?: boolean;
  onSave: (text: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  className?: string;
  title?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastTap = useRef(0);

  const grow = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    if (!editing) return;
    setDraft(value);
    const el = singleLine ? inputRef.current : areaRef.current;
    if (!el) return;
    el.focus();
    (el as HTMLInputElement | HTMLTextAreaElement).setSelectionRange(value.length, value.length);
    if (!singleLine) grow(el as HTMLTextAreaElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const commit = (text = draft) => {
    setEditing(false);
    const next = singleLine ? text.replace(/\s+/g, " ").trim() : text;
    if (next.trim() !== value.trim() && next.trim()) onSave(next);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(value);
  };

  const Actions = (
    <span className="os-edit-actions" data-noswipe>
      {/* onMouseDown preventDefault so the field doesn't blur before the click */}
      <button type="button" aria-label="save" title="save (Enter)" className="os-edit-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => commit()}>
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
          <path d="M2.6 6.9 5.1 9.4 10.4 3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button type="button" aria-label="cancel" title="cancel (Esc)" className="os-edit-btn" onMouseDown={(e) => e.preventDefault()} onClick={cancel}>
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
          <path d="M3.4 3.4 9.6 9.6M9.6 3.4 3.4 9.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
    </span>
  );

  if (editing) {
    if (singleLine) {
      // names: an auto-WIDTH field; the ✓/✕ inline at the right.
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem", maxWidth: "100%" }}>
          <input
            ref={inputRef}
            data-noswipe
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/\n/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(e.currentTarget.value); }
              else if (e.key === "Escape") { e.preventDefault(); cancel(); }
              e.stopPropagation();
            }}
            placeholder={placeholder}
            className={["os-edit-area", "os-edit-name", className].filter(Boolean).join(" ")}
            style={style}
            spellCheck={false}
            autoComplete="off"
          />
          {Actions}
        </span>
      );
    }
    // summaries: an auto-HEIGHT textarea; the ✓/✕ on the BOTTOM (column).
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: "0.45rem", maxWidth: "100%", width: "100%" }}>
        <textarea
          ref={areaRef}
          data-noswipe
          value={draft}
          onChange={(e) => { setDraft(e.target.value); grow(e.target); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); }
            else if (e.key === "Escape") { e.preventDefault(); cancel(); }
            e.stopPropagation();
          }}
          placeholder={placeholder}
          className={["os-edit-area", className].filter(Boolean).join(" ")}
          style={{ resize: "none", overflow: "hidden", width: "100%", ...style }}
          rows={Math.max(1, draft.split("\n").length)}
        />
        {Actions}
      </span>
    );
  }

  return (
    <span
      className={["annotate", className].filter(Boolean).join(" ")}
      data-manual={manual ? "true" : undefined}
      title={title ?? (manual ? "you wrote this — it carries more weight" : "double-click to edit")}
      // a single click on a NAME is the rename zone — never let it bubble to the
      // row/card's open handler (single-click-to-open lives on the rest of the item).
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
      onTouchEnd={(e) => {
        const now = Date.now();
        if (now - lastTap.current < 320) { e.preventDefault(); e.stopPropagation(); setEditing(true); }
        lastTap.current = now;
      }}
      style={{ display: "inline", ...style }}
    >
      {value || <span style={{ color: "var(--ink-4)" }}>{placeholder}</span>}
    </span>
  );
}

// EditableName — the singleLine wrapper for folder / file / section / card names.
// Inline rename in place; grows with the text. Pass onSave to persist.
export function EditableName(props: Omit<Parameters<typeof EditableText>[0], "singleLine">) {
  return <EditableText {...props} singleLine placeholder={props.placeholder ?? "name…"} />;
}
